import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"

import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// Actions
const connectPayPalRequested = createAction("payment-provider/connectPayPalRequested")
const connectPayPalReceived = createAction("payment-provider/connectPayPalReceived")
const connectPayPalFailed = createAction("payment-provider/connectPayPalFailed")

// PayPal Orders
const createPayPalOrderRequested = createAction("payment-provider/createPayPalOrderRequested")
const createPayPalOrderReceived = createAction("payment-provider/createPayPalOrderReceived")
const createPayPalOrderFailed = createAction("payment-provider/createPayPalOrderFailed")

const capturePayPalOrderRequested = createAction("payment-provider/capturePayPalOrderRequested")
const capturePayPalOrderReceived = createAction("payment-provider/capturePayPalOrderReceived")
const capturePayPalOrderFailed = createAction("payment-provider/capturePayPalOrderFailed")

const disconnectPayPalRequested = createAction("payment-provider/disconnectPayPalRequested")
const disconnectPayPalReceived = createAction("payment-provider/disconnectPayPalReceived")
const disconnectPayPalFailed = createAction("payment-provider/disconnectPayPalFailed")

// PowerTranz (guide-owned merchant accounts, DS-4717)
// One requested/received/failed trio and one loading slot per operation, like the PayPal ones above.
const POWERTRANZ_OPERATIONS = [
  "getPowerTranzAccountStatus",
  "connectPowerTranz",
  "disconnectPowerTranz",
  "verifyPowerTranz",
  "savePowerTranzCardFee",
  "createPowerTranzPayment",
  "getPowerTranzPaymentStatus",
  "chargePowerTranzSavedCard",
  "removePowerTranzSavedCard"
]

const powerTranzActions = POWERTRANZ_OPERATIONS.reduce(
  (acc, operation) => ({
    ...acc,
    [operation]: {
      requested: createAction(`payment-provider/${operation}Requested`),
      received: createAction(`payment-provider/${operation}Received`),
      failed: createAction(`payment-provider/${operation}Failed`)
    }
  }),
  {}
)

// Every account endpoint answers with the account as it now stands (integration guide 4.1).
const ACCOUNT_DATA_OPERATIONS = ["getPowerTranzAccountStatus", "connectPowerTranz", "verifyPowerTranz", "disconnectPowerTranz", "savePowerTranzCardFee"]

// Builds the reducer cases for each PowerTranz operation's trio.
const powerTranzCases = POWERTRANZ_OPERATIONS.reduce((acc, operation) => {
  const { requested, received, failed } = powerTranzActions[operation]
  return {
    ...acc,
    [requested.type]: (state) => {
      setRequested(state[operation])
    },
    [received.type]: (state) => {
      setReceived(state[operation])
    },
    [failed.type]: (state) => {
      setFailed(state[operation])
    }
  }
}, {})

// Reducer
const initialState = {
  ...LOADING_INITIAL_STATE,
  connectPayPal: {
    ...LOADING_INITIAL_STATE
  },
  createPayPalOrder: {
    ...LOADING_INITIAL_STATE
  },
  capturePayPalOrder: {
    ...LOADING_INITIAL_STATE
  },
  disconnectPayPal: {
    ...LOADING_INITIAL_STATE
  },
  ...POWERTRANZ_OPERATIONS.reduce((acc, operation) => ({ ...acc, [operation]: { ...LOADING_INITIAL_STATE } }), {}),
  // The guide's PowerTranz account as the account endpoints last returned it. GET /user caches it
  // for up to 5 minutes, so the settings tab reads this instead (integration guide 3.1).
  powerTranzAccount: {
    // null until the status call answers; true when the API's PowerTranz switch is on, false on 404.
    isAvailable: null,
    // powertranz_account_data as sent (null = never connected)
    data: null,
    // The response's top-level is_eligible, which also covers a guide who never connected.
    isEligible: null,
    // The response's top-level checkout_payment_provider: who guests pay through ("powertranz",
    // "stripe", "paypal"; null = none, or an API that doesn't send it yet).
    checkoutPaymentProvider: null
  }
}

const paymentProviderReducer = createReducer(initialState, {
  [connectPayPalRequested.type]: (state) => {
    setRequested(state.connectPayPal)
  },
  [connectPayPalReceived.type]: (state) => {
    setReceived(state.connectPayPal)
  },
  [connectPayPalFailed.type]: (state) => {
    setFailed(state.connectPayPal)
  },
  // Create Order
  [createPayPalOrderRequested.type]: (state) => {
    setRequested(state.createPayPalOrder)
  },
  [createPayPalOrderReceived.type]: (state) => {
    setReceived(state.createPayPalOrder)
  },
  [createPayPalOrderFailed.type]: (state) => {
    setFailed(state.createPayPalOrder)
  },
  // Capture Order
  [capturePayPalOrderRequested.type]: (state) => {
    setRequested(state.capturePayPalOrder)
  },
  [capturePayPalOrderReceived.type]: (state) => {
    setReceived(state.capturePayPalOrder)
  },
  [capturePayPalOrderFailed.type]: (state) => {
    setFailed(state.capturePayPalOrder)
  },
  // Disconnect PayPal
  [disconnectPayPalRequested.type]: (state) => {
    setRequested(state.disconnectPayPal)
  },
  [disconnectPayPalReceived.type]: (state) => {
    setReceived(state.disconnectPayPal)
  },
  [disconnectPayPalFailed.type]: (state) => {
    setFailed(state.disconnectPayPal)
  },
  ...powerTranzCases,
  ...ACCOUNT_DATA_OPERATIONS.reduce(
    (acc, operation) => ({
      ...acc,
      [powerTranzActions[operation].received.type]: (state, { payload }) => {
        setReceived(state[operation])
        state.powerTranzAccount.isAvailable = true
        state.powerTranzAccount.data = payload?.powertranz_account_data ?? null
        state.powerTranzAccount.isEligible = typeof payload?.is_eligible === "boolean" ? payload.is_eligible : null
        state.powerTranzAccount.checkoutPaymentProvider = payload?.checkout_payment_provider || null
      }
    }),
    {}
  ),
  [powerTranzActions.getPowerTranzAccountStatus.failed.type]: (state, { payload }) => {
    setFailed(state.getPowerTranzAccountStatus)
    // 404 = the API's PowerTranz switch is off: hide PowerTranz (integration guide 1, 4).
    if (payload?.status === 404) state.powerTranzAccount.isAvailable = false
  }
})

export default paymentProviderReducer

export const connectPayPal = (source) =>
  api({
    url: "/guide/paypal_accounts/start_onboarding",
    method: "post",
    data: { source },
    onStart: connectPayPalRequested.type,
    onSuccess: connectPayPalReceived.type,
    onError: connectPayPalFailed.type
  })

export const disconnectPayPal = () =>
  api({
    url: "/guide/paypal_accounts/disconnect",
    method: "put",
    onStart: disconnectPayPalRequested.type,
    onSuccess: disconnectPayPalReceived.type,
    onError: disconnectPayPalFailed.type
  })

// Create PayPal Order
// `guestToken`: an order's guest token (the widget's order of several bookings); without it the
// request carries the stored token, as for a single booking.
export const createPayPalOrder = (data, { guestToken } = {}) =>
  api({
    url: "/paypal/create_order",
    method: "post",
    data,
    guestToken,
    onStart: createPayPalOrderRequested.type,
    onSuccess: createPayPalOrderReceived.type,
    onError: createPayPalOrderFailed.type
  })

// Capture PayPal Order
export const capturePayPalOrder = (data, { guestToken } = {}) =>
  api({
    url: `/paypal/capture_order`,
    method: "post",
    data,
    guestToken,
    onStart: capturePayPalOrderRequested.type,
    onSuccess: capturePayPalOrderReceived.type,
    onError: capturePayPalOrderFailed.type
  })

// PowerTranz calls show their own per-code messages (powertranz.errors.*), so the global toast
// is suppressed on every one of them.
const powerTranzApi = (operation, config) =>
  api({
    ...config,
    suppressErrorNotification: true,
    onStart: powerTranzActions[operation].requested.type,
    onSuccess: powerTranzActions[operation].received.type,
    onError: powerTranzActions[operation].failed.type
  })

export const getPowerTranzAccountStatus = () =>
  powerTranzApi("getPowerTranzAccountStatus", { url: "/guide/powertranz_accounts/status" })

// data: { powertranz_id, powertranz_password, page_set, page_name }
export const connectPowerTranz = (data) =>
  powerTranzApi("connectPowerTranz", { url: "/guide/powertranz_accounts/connect", method: "post", data })

export const disconnectPowerTranz = () =>
  powerTranzApi("disconnectPowerTranz", { url: "/guide/powertranz_accounts/disconnect", method: "put" })

export const verifyPowerTranz = () => powerTranzApi("verifyPowerTranz", { url: "/guide/powertranz_accounts/verify", method: "post" })

// The processing fee (percent) the guide passes on with PowerTranz charges. data: { card_fee_rate }
export const savePowerTranzCardFee = (data) =>
  powerTranzApi("savePowerTranzCardFee", { url: "/guide/powertranz_accounts/card_fee", method: "put", data })

// data: { booking_id, promo_codes?, link_token?, save_card_consent? }
export const createPowerTranzPayment = (data) =>
  powerTranzApi("createPowerTranzPayment", { url: "/powertranz/create_payment", method: "post", data })

// params: { booking_id } or { attempt_ref }
export const getPowerTranzPaymentStatus = (params) => powerTranzApi("getPowerTranzPaymentStatus", { url: "/powertranz/status", params })

// data: { booking_id, income_id }
export const chargePowerTranzSavedCard = (data) =>
  powerTranzApi("chargePowerTranzSavedCard", { url: "/powertranz/charge_saved_card", method: "post", data })

export const removePowerTranzSavedCard = (id) =>
  powerTranzApi("removePowerTranzSavedCard", { url: `/powertranz/saved_cards/${id}`, method: "delete" })
